import { html, css, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/**
 * Displays a QR code and join code for session sharing.
 * Uses the global QRCode library loaded via CDN.
 */
@customElement('join-info')
export class JoinInfo extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 1rem;
            }

            .qr-container {
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: white;
                padding: 0.25rem;
                border-radius: 0.375rem;
                width: fit-content;
            }

            .join-code {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 0.25rem;
                background-color: var(--bg-secondary);
                padding: 0.5rem 1rem;
                border-radius: 0.5rem;
                border: 2px solid var(--primary-color);
                max-height: 64px;
            }

            .join-code-value {
                font-size: 2rem;
                font-weight: 700;
                color: var(--primary-color);
                letter-spacing: 0.15em;
                font-family: 'Courier New', Courier, monospace;
            }

            @media (min-width: 768px) {
                .qr-container {
                    margin: 0;
                }
            }

            @media (max-width: 430px) and (orientation: portrait) {
                .qr-container canvas {
                    max-width: 120px;
                    width: 120px !important;
                    height: 120px !important;
                }
            }
        `,
    ];

    @property({ type: String, attribute: 'join-code' })
    joinCode = '';

    @property({ type: String, attribute: 'server-url' })
    serverUrl = '';

    updated(changed: PropertyValues): void {
        if (changed.has('serverUrl') && this.serverUrl) {
            this._generateQRCode();
        }
    }

    private _generateQRCode(): void {
        const container = this.shadowRoot?.querySelector('.qr-container') as HTMLElement;
        if (!container) return;
        container.innerHTML = '';
        try {
            const QRCode = (window as any).QRCode;
            if (typeof QRCode === 'undefined') {
                console.warn('QRCode library not loaded');
                return;
            }
            new QRCode(container, {
                text: this.serverUrl,
                width: 56,
                height: 56,
                colorDark: '#000000',
                colorLight: '#ffffff',
                correctLevel: QRCode.CorrectLevel.M,
            });
        } catch (error) {
            console.warn('Failed to generate QR code:', error);
        }
    }

    render() {
        return html`
            <div class="qr-container"></div>
            <div class="join-code">
                <span class="join-code-value">${this.joinCode || '---'}</span>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'join-info': JoinInfo;
    }
}
